import type { DailyTimeBlock } from '../data/leadDistribution'

export type { DailyTimeBlock } from '../data/leadDistribution'

export interface Weekday {
  /** 0 = Sunday, matching Date.prototype.getDay(). */
  index: number
  /** Compact label used on the weekday toggles. */
  short: string
  name: string
}

export const WEEKDAYS: Weekday[] = [
  { index: 1, short: 'Mo', name: 'Monday' },
  { index: 2, short: 'Tu', name: 'Tuesday' },
  { index: 3, short: 'We', name: 'Wednesday' },
  { index: 4, short: 'Th', name: 'Thursday' },
  { index: 5, short: 'Fr', name: 'Friday' },
  { index: 6, short: 'Sa', name: 'Saturday' },
  { index: 0, short: 'Su', name: 'Sunday' },
]

export const WEEKDAY_PRESET: number[] = [1, 2, 3, 4, 5]

export function isTimeBlockActive(block: DailyTimeBlock, now: Date): boolean {
  if (!isValidTimeBlock(block) || !hasWeekdays(block)) return false
  if (!block.days.includes(now.getDay())) return false

  const currentMinutes = now.getHours() * 60 + now.getMinutes()
  return currentMinutes >= toMinutes(block.start) && currentMinutes < toMinutes(block.end)
}

export function isScheduledNow(blocks: DailyTimeBlock[], now: Date): boolean {
  return blocks.some((block) => isTimeBlockActive(block, now))
}

export function isValidTimeBlock(block: DailyTimeBlock): boolean {
  return isTimeValue(block.start) && isTimeValue(block.end) && toMinutes(block.start) < toMinutes(block.end)
}

export function hasWeekdays(block: DailyTimeBlock): boolean {
  return block.days.length > 0
}

export function formatTimeBlock(block: DailyTimeBlock): string {
  return `${formatWeekdays(block.days)} ${formatTime(block.start)} – ${formatTime(block.end)}`.trim()
}

export function formatWeekdays(days: number[]): string {
  if (days.length === 0) return 'No days'
  if (days.length === 7) return 'Every day'

  const ordered = WEEKDAYS.filter((weekday) => days.includes(weekday.index))
  if (ordered.length === 5 && WEEKDAY_PRESET.every((day) => days.includes(day))) return 'Weekdays'
  return ordered.map((weekday) => weekday.short).join(', ')
}

export function toggleWeekday(days: number[], dayIndex: number): number[] {
  return days.includes(dayIndex)
    ? days.filter((day) => day !== dayIndex)
    : [...days, dayIndex].sort((first, second) => weekdayOrder(first) - weekdayOrder(second))
}

function weekdayOrder(dayIndex: number): number {
  return WEEKDAYS.findIndex((weekday) => weekday.index === dayIndex)
}

function isTimeValue(value: string): boolean {
  return /^([01]\d|2[0-3]):[0-5]\d$/.test(value)
}

function toMinutes(value: string): number {
  const [hours, minutes] = value.split(':').map(Number)
  return hours * 60 + minutes
}

function formatTime(value: string): string {
  const [hours, minutes] = value.split(':').map(Number)
  const period = hours >= 12 ? 'PM' : 'AM'
  const displayHour = hours % 12 || 12
  return `${displayHour}:${String(minutes).padStart(2, '0')} ${period}`
}
